import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@/UI/Box/Box';
import { H1, TextLgRg, TextSmMd, Typography } from '@/UI/AppTypography/AppTypography';
import { Grid } from '@/UI/Grid/Grid';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType, BillingCycle } from '@/types/shared';
import { PricingPackage } from '@/components/SubscriptionsAndPayments/PricingPage/PricingPackage';
import EnterprisePackage from '@/components/SubscriptionsAndPayments/PricingPage/EnterprisePackage';
import CreditsTable from '@/components/SubscriptionsAndPayments/PricingPage/CreditsTable';
import PricingFAQS from '@/components/SubscriptionsAndPayments/PricingPage/PricingFAQS';
import { AuthContext } from '@/contexts/AuthContext';
import { PricingPageContainer } from '@/enums/pricing-page.enum';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { FlexColCenHor, FlexCenHor, FlexColCenHorVer } from '@/UI/styles';

const getPricingPackages = (currentUserPlan: SubscriptionType) => {
  const basePackages = [
    {
      name: SubscriptionType.Free,
      description: "Explore Figma Weave's possibilities.",
      monthlyPrice: 0,
      annualPrice: 0,
      credits: '150 monthly credits',
      creditsInfo: '= 375 images or 25 sec video',
      features: ['Access to all AI models', 'Professional-grade editing tools', '5 workflows', 'No workflow history'],
      highlightedFeature: 3,
    },
    {
      name: SubscriptionType.Starter,
      description: 'For creators producing content occasionally.',
      monthlyPrice: 24,
      annualPrice: 19,
      credits: '1,500 monthly credits',
      creditsInfo: '= 3,750 images or 417 sec video',
      featuresTitle: 'Everything in Free, plus -',
      features: ['Unlimited workflows', 'Top up at $10 / 1,000 credits'],
    },
    {
      name: SubscriptionType.Pro,
      description: 'For professionals generating media daily.',
      monthlyPrice: 45,
      annualPrice: 36,
      credits: '4,000 monthly credits',
      creditsInfo: '= 10,000 images or 1,111 sec video',
      featuresTitle: 'Everything in Starter, plus -',
      features: ['3-month credit rollover', 'Top up at $10 / 1,200 credits'],
      highlightedFeatureIndex: 1,
      isPopular: true,
    },
    {
      name: SubscriptionType.Team,
      description: 'For in-house and agency visual teams.',
      monthlyPrice: 60,
      annualPrice: 48,
      credits: '4,500 monthly credits / user',
      creditsInfo: '= 11,250 images or 1,250 sec video',
      featuresTitle: 'Everything in Pro, plus -',
      features: [
        'Unified billing',
        'Shared credit pool for maximal efficiency',
        'Workspace file sharing',
        'Team members administration',
        'Team credits management',
      ],
    },
  ];

  return basePackages.map((pkg) => {
    // If current user is on Free plan and this is the Starter package
    if (currentUserPlan !== SubscriptionType.Free && pkg.name === SubscriptionType.Starter) {
      return {
        ...pkg,
        featuresTitle: '',
        features: [
          'Full access to all AI models',
          'Professional-grade editing tools',
          'Workflow collaboration feature',
          'Commercial license',
          'Unlimited workflows',
          'Top up at $10 / 1,000 credits',
        ],
      };
    }

    return pkg;
  });
};

export const PricingTablePage = ({ container }: { container: PricingPageContainer }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const { track } = useAnalytics();
  const [currentUserPlan] = useState(currentUser?.activeWorkspace?.subscription?.type || SubscriptionType.Free);
  const [currentUserBillingCycle] = useState(
    currentUser?.activeWorkspace?.subscription?.billingCycle || BillingCycle.Yearly,
  );
  const { isVisible: isBannerVisible } = useBanner(APP_BANNER_ID);
  const [billingCycle, setBillingCycle] = useState(BillingCycle.Yearly);
  const { offset: bannerOffset } = useBanner(APP_BANNER_ID);
  const MAX_WIDTH = currentUserPlan !== SubscriptionType.Free ? '936px' : '1248px';

  useEffect(() => {
    track(
      'Opened_pricing_table',
      {
        context: 'Payments',
        context_data: { container },
      },
      TrackTypeEnum.BI,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBillingCycleChange = (newBillingCycle: BillingCycle | null) => {
    track('Pricing Table - Changed_billing_cycle', { billingCycle: newBillingCycle }, TrackTypeEnum.BI);
    if (newBillingCycle) {
      setBillingCycle(newBillingCycle);
    }
  };

  return (
    <>
      <FlexColCenHor
        data-testid="pricing-table-page-container"
        sx={{
          width: '100%',
          overflowY: 'auto',
          background: `${color.Black100}`,
          backdropFilter: `blur(3px)`,
          WebkitBackdropFilter: `blur(3px)`,
          p: {
            xs: 4,
            md: 12,
          },
        }}
      >
        <H1 sx={{ fontSize: '3rem', fontWeight: 600, mb: 4, mt: 6 }}>
          {currentUser ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TITLE) : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TEMP_TITLE)}
        </H1>
        {/* Toggle Monthly / Annually */}
        <Box sx={{ mb: 6, mt: 4 }}>
          <AppToggleButtons
            btnH={32}
            btnW={136}
            sx={{
              backgroundColor: color.White04_T,
              borderRadius: 2,
              p: 0.5,
            }}
            value={billingCycle}
            options={[
              {
                value: BillingCycle.Monthly,
                label: <TextSmMd color="inherit">{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.MONTHLY)}</TextSmMd>,
                'aria-label': t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.MONTHLY),
              },
              {
                value: BillingCycle.Yearly,
                label: <TextSmMd color="inherit">{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY)}</TextSmMd>,
                'aria-label': t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY),
              },
            ]}
            mode="light"
            onChange={handleBillingCycleChange}
          />
        </Box>
        {/* Packages */}
        <Grid
          container
          data-testid="pricing-table-packages-container"
          spacing={2}
          sx={{
            maxWidth: MAX_WIDTH,
            mb: 8,
          }}
        >
          {getPricingPackages(currentUserPlan)
            .filter(
              (pricingPackage) =>
                currentUserPlan === SubscriptionType.Free || pricingPackage.name !== SubscriptionType.Free,
            )
            .map((pricingPackage) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4, lg: currentUserPlan === SubscriptionType.Free ? 3 : 4 }}
                key={pricingPackage.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <PricingPackage
                  subscriptionType={pricingPackage.name}
                  description={pricingPackage.description}
                  monthlyPrice={pricingPackage.monthlyPrice}
                  annualPrice={pricingPackage.annualPrice}
                  credits={pricingPackage.credits}
                  creditsInfo={pricingPackage.creditsInfo}
                  featuresTitle={pricingPackage.featuresTitle}
                  features={pricingPackage.features}
                  highlightedFeatureIndex={pricingPackage.highlightedFeatureIndex}
                  billingCycle={billingCycle}
                  currentUserPlan={currentUserPlan}
                  currentUserBillingCycle={currentUserBillingCycle}
                  isPopular={pricingPackage.isPopular}
                  workspaceId={currentUser?.activeWorkspace?.workspaceId}
                  downgradeData={{
                    date: currentUser?.activeWorkspace?.subscription?.scheduledDowngradeDate,
                    plan: currentUser?.activeWorkspace?.subscription?.scheduledDowngradePlan,
                  }}
                  currentUser={currentUser || undefined}
                />
              </Grid>
            ))}
        </Grid>
        {/* Enterprise Package */}
        <FlexCenHor sx={{ width: '100%', maxWidth: MAX_WIDTH, mb: 6 }}>
          <EnterprisePackage currentUser={currentUser || undefined} />
        </FlexCenHor>

        {/* Holiday Promo Disclaimer */}
        {isBannerVisible && (
          <Box sx={{ width: { xs: '90%', md: '720px' }, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: color.White64_T,
              }}
            >
              {t(I18N_KEYS.WEAVY_MARKETING.APP_BANNER.HOLIDAY_PROMO.DISCLAIMER)}
            </Typography>
          </Box>
        )}
        {/* Partners */}

        <FlexColCenHorVer
          sx={{
            width: '100%',
            pt: 9,
            mb: 6,
          }}
        >
          <FlexCenHor sx={{ width: '100%', mb: 8 }}>
            <TextLgRg>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PARTNERS_TITLE)}</TextLgRg>
          </FlexCenHor>
          <FlexCenHor
            sx={{
              gap: 0,
              flexWrap: 'wrap',
              width: '75%',
            }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <img src={`/partners-logos/partner_logo_0${index + 1}.svg`} alt={`Logo ${index + 1}`} key={index} />
            ))}
            {Array.from({ length: 6 }).map((_, index) => (
              <img src={`/partners-logos/partner_logo_0${index + 7}.svg`} alt={`Logo ${index + 7}`} key={index + 7} />
            ))}
          </FlexCenHor>
        </FlexColCenHorVer>

        <FlexColCenHor sx={{ width: '100%', pt: 9, mb: 6 }}>
          <FlexCenHor sx={{ width: '100%', mb: 8 }}>
            <TextLgRg>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.CREDITS_TABLE_TITLE)}</TextLgRg>
          </FlexCenHor>
          <Box>
            <CreditsTable maxWidth={720} />
          </Box>
        </FlexColCenHor>
        <FlexColCenHor sx={{ width: '100%', pt: 9, mb: 6 }}>
          <FlexCenHor sx={{ width: '100%', mb: 8 }}>
            <Typography variant="body-lg-rg">{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.FAQ_TITLE)}</Typography>
          </FlexCenHor>
          <FlexCenHor sx={{ width: '100%' }}>
            <PricingFAQS maxWidth={720} />
          </FlexCenHor>
        </FlexColCenHor>
      </FlexColCenHor>
      {container === PricingPageContainer.Page && (
        <FlexColCenHor data-testid="pricing-table-page-home-button" sx={{ width: '100%' }}>
          <AppIconButton
            onClick={() => navigate('/')}
            sx={{ position: 'fixed', top: 17 + bannerOffset, left: 16, zIndex: 1 }}
          >
            <img src="/icons/logo.svg" alt="figma_weave_logo" />
          </AppIconButton>
        </FlexColCenHor>
      )}
    </>
  );
};
